import { useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';

// http(s) → ws(s)
function toWsUrl(base) {
  return base.replace(/^http/, 'ws') + '/api/ws';
}

/**
 * useWebSocket — lidhet me backend WebSocket dhe thërret onMessage
 * kur vjen reading i ri nga Pi.
 *
 * @param {function} onMessage - callback({ sensor_id, data_logger_id, ts, temperature, humidity })
 * @param {boolean} enabled - mund të çaktivizohet me false
 */
export function useWebSocket(onMessage, enabled = true) {
  const wsRef       = useRef(null);
  const retryRef    = useRef(null);
  const delayRef    = useRef(2000);   // backoff: 2s → 60s
  const onMsgRef    = useRef(onMessage);
  const pingRef     = useRef(null);
  const mountedRef  = useRef(true);

  // Mbaj referncën aktuale pa rifidh koneksionin
  useEffect(() => { onMsgRef.current = onMessage; }, [onMessage]);

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled) return;

    const ws = new WebSocket(toWsUrl(BACKEND_URL));
    wsRef.current = ws;

    ws.onopen = () => {
      delayRef.current = 2000;
      // Ping çdo 25s për ta mbajtur lidhjen gjallë
      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25000);
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        onMsgRef.current?.(data);
      } catch {
        // ping/pong ose mesazh jo-JSON — injoro
      }
    };

    ws.onclose = () => {
      clearInterval(pingRef.current);
      if (!mountedRef.current) return;
      // Rilidhu me exponential backoff
      retryRef.current = setTimeout(() => {
        delayRef.current = Math.min(delayRef.current * 2, 60000);
        connect();
      }, delayRef.current);
    };

    ws.onerror = () => ws.close();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      clearTimeout(retryRef.current);
      clearInterval(pingRef.current);
      wsRef.current?.close();
    };
  }, [connect, enabled]);
}
